import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { InvoicesStatus } from '@spike/invoices-action';
import { getDropInDepositSessionThunk } from '@spike/payments-action';
import { submitOnlineBookingPaymentThunk } from 'actions/onlineBooking/OnlineBookingActions';
import { AdyenDropIn } from 'components/AdyenDropIn/AdyenDropIn';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import { Alert } from 'components/UI/Alert/Alert';
import { Spinner } from 'components/UI/Spinner/Spinner';
import { useApiClientWrapper } from 'hooks/useApiClientWrapper';
import { usePhoneFormat } from 'hooks/usePhoneFormat';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getMarketplace } from 'reducers/marketplace/MarketplaceSelectors';
import { RootState } from 'store';

export const OnlineBookingPaymentStep: React.FC = () => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const marketplace = useSelector(getMarketplace);
    const dropIn = useSelector((state: RootState) => state.payments.dropIn);
    const { status: invoicesStatus, depositInvoice } = useSelector(
        (state: RootState) => state.invoices
    );

    const formattedPhone = usePhoneFormat(marketplace.phone ?? '');

    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [hasError, setHasError] = React.useState<boolean>(false);

    React.useEffect(() => {
        switch (invoicesStatus) {
            case InvoicesStatus.CreateDepositInvoiceSuccess:
                if (!depositInvoice || dropIn?.id || isLoading) {
                    return;
                }

                setIsLoading(true);
                dispatch(getDropInDepositSessionThunk(apiClientWrapper, Number(depositInvoice.id)));
                break;
            case InvoicesStatus.Error:
                setIsLoading(false);
                setHasError(true);
                break;
        }
        // eslint-disable-next-line
    }, [invoicesStatus, depositInvoice, apiClientWrapper, dropIn, isLoading]);

    const dropInErrorHandler = () => {
        setHasError(true);
    };

    const dropInSuccessHandler = (sessionId: string) => {
        setIsLoading(true);
        dispatch(submitOnlineBookingPaymentThunk(Number(depositInvoice?.id), sessionId));
    };

    return (
        <>
            <LeftColumn contentClassName="flex flex-col flex-1">
                <h1 className="h1 underlined w-full mb-6">Payment info</h1>

                {isLoading && !dropIn && (
                    <div className="w-full text-center">
                        <Spinner size={50} />
                    </div>
                )}

                {hasError && (
                    <Alert variant="error" icon={faTimesCircle} className="mb-5">
                        <b>Payment cannot be processed.</b> Please try a different card or contact{' '}
                        <b>{marketplace.name}</b> directly to request an appointment using a
                        different payment method. If you have any questions, contact{' '}
                        <b>{marketplace.name}</b> at
                        <a href={`tel:${marketplace.phone}`} className="no-underline">
                            {formattedPhone}
                        </a>
                        .
                    </Alert>
                )}

                {dropIn && (
                    <AdyenDropIn
                        dropIn={dropIn}
                        onError={dropInErrorHandler}
                        onSuccess={dropInSuccessHandler}
                    />
                )}
            </LeftColumn>

            <RightColumn>
                <div className="flex flex-col gap-5">
                    <MarketplaceInfo
                        inverted
                        marketplace={marketplace}
                        className="hide-on-mobile-and-tablet"
                    />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </>
    );
};
